import { PureComponent, ReactNode } from 'react';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { ThunkDispatch } from 'redux-thunk';
import { AntTooltip } from '../../../components/common/AntTooltip';
import { magicBoxtoggleHandler } from '../../../components/prescriptionPad/rightPanel/workspace/NavBar';
import { Actions } from '../../../store/actions';
import { updateMagicBoxActiveTab } from '../../../store/actions/prescriptionPad';
import { GlobalState } from '../../../store/reducers';
import { PatientDetailsMR, PatientDetailsVisits } from '../../../store/reducers/prescriptionPad';
import {
    getPatientDetailsMR,
    getPatientDetailsVisits,
    magicBoxActiveTab,
} from '../../../store/selectors/prescriptionPad';
import { track } from '../../../tracking';
import { PPRouterProps } from '../../../routes/paths';
import { MAGIC_BOX_COMPONENTS } from './MAGIC_BOX_COMPONENTS';
import { configs } from './MagicBoxConfigs';
import { getGenieSetting } from '../../../store/selectors/session';

type Props = {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
    mr?: PatientDetailsMR;
    activeTab?: string;
    hidePatientHealthSummary?: boolean;
    patientDetails?: PatientDetailsVisits;
};

class MagicBox extends PureComponent<Props> {
    setActiveTab = (tab: keyof typeof configs): void => {
        const { dispatch } = this.props;
        dispatch(updateMagicBoxActiveTab(tab as MAGIC_BOX_COMPONENTS));
        track('MAGIC_BOX_SEE_ALL', { activeTab: tab.toString() });
    };

    render(): ReactNode {
        const { activeTab = MAGIC_BOX_COMPONENTS.VITALS, hidePatientHealthSummary } = this.props;
        const { mr, patientDetails } = this.props;
        const notesCount = patientDetails?.data?.filter((visit) => visit.privateNotes)?.length ?? 0;

        return (
            <div className="relative flex flex-col w-full bg-white rounded-8 font-lato">
                <div>
                    <div
                        style={{
                            background: !hidePatientHealthSummary
                                ? 'linear-gradient(90deg, #205CFF 29.67%, #CD9BEA 100%)'
                                : undefined,
                        }}
                        className="rounded-t-8 w-full h-36 flex items-center space-x-12 bg-darwin-accent-bright-blue-800 text-white px-16"
                    >
                        {Object.keys(configs).map((tab, index) => (
                            <AntTooltip
                                key={tab + activeTab + index}
                                placement="top"
                                content={configs[tab as keyof typeof configs].label}
                                visible={activeTab === tab ? false : undefined}
                            >
                                <div
                                    onClick={(): void =>
                                        this.setActiveTab(tab as keyof typeof configs)
                                    }
                                    className={`w-24 h-24 flex justify-center items-center rounded-full text-12 cursor-pointer magicbox-button relative ${
                                        activeTab === tab ? 'magicbox-active-button' : ''
                                    }`}
                                >
                                    <span className="font-700">
                                        {configs[tab as keyof typeof configs].initial}
                                    </span>
                                    <p className="font-400 hidden">
                                        {configs[tab as keyof typeof configs].label}
                                    </p>
                                    {tab === MAGIC_BOX_COMPONENTS.MEDICAL_RECORDS &&
                                        activeTab !== MAGIC_BOX_COMPONENTS.MEDICAL_RECORDS  &&
                                        mr?.data &&
                                        mr.data.length > 0 && (
                                            <span className="absolute -top-1 -right-2 flex items-center justify-center h-8 w-8 min-w-8 bg-darwin-accent-medical-history-orange-800 text-white rounded-full font-700">
                                            </span>
                                        )}
                                    {tab === MAGIC_BOX_COMPONENTS.MEDICAL_HISTORY &&
                                        activeTab !== MAGIC_BOX_COMPONENTS.MEDICAL_HISTORY &&
                                        notesCount > 0 && (
                                            <span className="absolute -top-4 -right-16 flex items-center justify-center h-20 min-w-20 px-6 bg-darwin-accent-medical-history-orange-800 text-white rounded-full font-700">
                                                {notesCount}
                                            </span>
                                        )}
                                </div>
                            </AntTooltip>
                        ))}
                    </div>
                    {<SeeAll activeTab={activeTab} />}
                </div>
                <div className="flex-1 font-lato font-400 text-13 overflow-scroll hide-scrollbar">
                    {configs[activeTab]?.component}
                </div>
                <div
                    onClick={magicBoxtoggleHandler}
                    className={`w-20 h-20 cursor-pointer flex justify-center items-center absolute bg-white border-1 border-darwin-neutral-200 rounded-full z-10 -top-9 -right-6`}
                >
                    <svg
                        className="w-9 h-11 fill-darwin-neutral-900"
                        viewBox="0 0 9 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path d="M7.72266 6.15234C8.00195 6.41016 8.00195 6.86133 7.72266 7.11914C7.59375 7.24805 7.42188 7.3125 7.25 7.3125C7.05664 7.3125 6.88477 7.24805 6.75586 7.11914L4.5 4.86328L2.22266 7.11914C2.09375 7.24805 1.92188 7.3125 1.75 7.3125C1.55664 7.3125 1.38477 7.24805 1.25586 7.11914C0.976562 6.86133 0.976562 6.41016 1.25586 6.15234L3.51172 3.875L1.25586 1.61914C0.976562 1.36133 0.976562 0.910156 1.25586 0.652344C1.51367 0.373047 1.96484 0.373047 2.22266 0.652344L4.5 2.9082L6.75586 0.652344C7.01367 0.373047 7.46484 0.373047 7.72266 0.652344C8.00195 0.910156 8.00195 1.36133 7.72266 1.61914L5.4668 3.89648L7.72266 6.15234Z" />
                    </svg>
                </div>
            </div>
        );
    }
}

type P = {
    activeTab: keyof typeof configs;
};

const SeeAll: React.FC<P> = ({ activeTab }) => {
    const history = useHistory();
    const params: PPRouterProps = useParams();
    const { pxId, pxState } = params;
    const onSeeAll = (): void => {
        track('MAGIC_BOX_SEE_ALL', { activeTab: activeTab.toString() });
        return configs[activeTab].focus(history, pxId, pxState);
    };
    return configs[activeTab].seeAllText ? (
        <div
            onClick={onSeeAll}
            className="flex items-center justify-between px-8 py-3 text-white text-12 font-400 cursor-pointer bg-darwin-primary-600"
        >
            <p>{configs[activeTab].seeAllText}</p>
            <svg
                role="img"
                viewBox="0 0 6 10"
                xmlns="http://www.w3.org/2000/svg"
                className="w-7 h-10 fill-white"
            >
                <path d="M1.25 9.625C1.07422 9.625 0.917969 9.56641 0.800781 9.44922C0.546875 9.21484 0.546875 8.80469 0.800781 8.57031L4.10156 5.25L0.800781 1.94922C0.546875 1.71484 0.546875 1.30469 0.800781 1.07031C1.03516 0.816406 1.44531 0.816406 1.67969 1.07031L5.42969 4.82031C5.68359 5.05469 5.68359 5.46484 5.42969 5.69922L1.67969 9.44922C1.5625 9.56641 1.40625 9.625 1.25 9.625Z"></path>
            </svg>
        </div>
    ) : null;
};

export default connect((state: GlobalState) => ({
    mr: getPatientDetailsMR(state),
    activeTab: magicBoxActiveTab(state),
    hidePatientHealthSummary: getGenieSetting(state, 'hidePatientHealthSummary') as boolean,
    patientDetails: getPatientDetailsVisits(state),
}))(MagicBox);
